// public/v3/src/components/user-edit-modal.jsx — add or edit a team member.
// Add mode provisions a Google Workspace SSO user (no password). Edit mode
// updates display name + role, and the Elation physician this person IS (089:
// the Note Expander's author of their drafts and swept notes, for a physician
// whose CCM and Elation emails differ; not the SMS Bot's "orders under"). Uses
// the shared unsaved-changes guard so a backdrop tap never silently discards
// typed input.
import React from 'react';
import { ccmApi } from '../lib/api';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';

export function UserEditModal({ mode, user, roles, onClose, onSubmit }) {
  const isAdd = mode === 'add';
  const [email, setEmail] = React.useState(user?.email || '');
  const [displayName, setDisplayName] = React.useState(user?.displayName || '');
  const [roleId, setRoleId] = React.useState(user?.roleId || '');
  const [physicianId, setPhysicianId] = React.useState(user?.elationPhysicianId || '');
  const [physicians, setPhysicians] = React.useState(null); // null loading | [...] | 'failed'
  // The server refused a statement that contradicts this person's Elation sign-in (409
  // contradicts_login): its words, and the admin's explicit override for the next save.
  const [contradiction, setContradiction] = React.useState('');
  const [override, setOverride] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (isAdd) return undefined;
    let live = true;
    ccmApi.listElationPhysicians()
      .then((list) => { if (live) setPhysicians(Array.isArray(list) ? list : []); })
      .catch(() => { if (live) setPhysicians('failed'); });
    return () => { live = false; };
  }, [isAdd]);

  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } =
    useUnsavedChangesGuard({ values: { email, displayName, roleId, physicianId }, onClose, busy });

  // Who the directory says this person already is: proven by their Elation sign-in, or stated.
  const list = Array.isArray(physicians) ? physicians : [];
  const provenAs = isAdd ? null : list.find((p) => p.provenUserId === user?.id) || null;
  const choosePhysician = (v) => { setPhysicianId(v); setContradiction(''); setOverride(false); };

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') attemptDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptDismiss]);

  async function submit() {
    setError('');
    if (isAdd && !email.trim()) { setError('Email is required.'); return; }
    if (!displayName.trim()) { setError('Name is required.'); return; }
    setBusy(true);
    try {
      if (isAdd) {
        // SSO-only provisioning: new users authenticate via Google Workspace.
        // Role assignment happens via a follow-up Edit once the user is created.
        await onSubmit({ email: email.trim(), displayName: displayName.trim(), role: 'staff' });
      } else {
        // Only a change is sent: a save that leaves it alone never asks Elation.
        const physicianChanged = physicianId !== (user?.elationPhysicianId || '');
        await onSubmit({
          displayName: displayName.trim(),
          ...(roleId ? { roleId } : {}),
          ...(physicianChanged ? { elationPhysicianId: physicianId || null } : {}),
          ...(physicianChanged && override ? { overrideLoginProof: true } : {}),
        });
      }
      onClose();
    } catch (e) {
      if (e?.status === 409 && e?.body?.reason === 'contradicts_login') setContradiction(e.message);
      else setError(e?.message || 'Save failed.');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div
        className="modal usermgmt-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isAdd ? 'Add team member' : 'Edit team member'}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}>
        <div className="modal-head">
          <h3>{isAdd ? 'Add team member' : 'Edit team member'}</h3>
        </div>
        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <h3 style={{ marginTop: 0 }}>Discard changes?</h3>
              <p>You've made changes but haven't saved yet.</p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={keepEditing}>Keep editing</button>
              <button className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              {isAdd ? (
                <div className="field">
                  <div className="field-label">Email</div>
                  <input
                    type="email"
                    placeholder="name@yourpractice.com"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={busy}
                  />
                </div>
              ) : (
                <div className="field">
                  <div className="field-label">Email</div>
                  <div style={{ padding: '8px 10px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)', fontSize: 14, color: 'var(--ink-3)' }}>
                    {user?.email}
                  </div>
                </div>
              )}
              <div className="field">
                <div className="field-label">Name</div>
                <input
                  type="text"
                  placeholder="Display name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={busy}
                  autoFocus={!isAdd}
                />
              </div>
              <div className="field">
                <div className="field-label">Role</div>
                <select value={roleId} onChange={(e) => setRoleId(e.target.value)} disabled={busy || isAdd}>
                  <option value="">— select a role —</option>
                  {(roles || []).map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              {!isAdd && (
                <div className="field">
                  <div className="field-label">Elation physician</div>
                  <select
                    aria-label="Elation physician"
                    value={physicianId}
                    onChange={(e) => choosePhysician(e.target.value)}
                    disabled={busy || !Array.isArray(physicians)}>
                    <option value="">— not a physician —</option>
                    {Array.isArray(physicians) && physicianId && !physicians.some((p) => p.id === physicianId) && (
                      <option value={physicianId}>Physician {physicianId} (not in Elation's list)</option>
                    )}
                    {(Array.isArray(physicians) ? physicians : []).map((p) => (
                      <option key={p.id} value={p.id}>{p.name}{p.npi ? ` (NPI ${p.npi})` : ''}</option>
                    ))}
                  </select>
                  <p className="usermgmt-hint">
                    {physicians === 'failed'
                      ? 'Elation could not be asked, so this cannot be changed right now.'
                      : provenAs && !physicianId
                        ? `Proven at sign-in: their Elation login is ${provenAs.name}'s. Nothing to set.`
                        : 'Who this person is in Elation, when their CCM and Elation emails differ: the Note Expander writes their swept notes and learns their style as them. Who someone orders under is set in SMS Bot.'}
                  </p>
                  {contradiction && (
                    <label className="usermgmt-hint" role="alert">
                      <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} disabled={busy} />
                      {' '}{contradiction}
                    </label>
                  )}
                </div>
              )}
              {isAdd && (
                <p className="usermgmt-hint">They sign in with Google Workspace — no password is set.</p>
              )}
              {error && (
                <div className="usermgmt-error" role="alert">{error}</div>
              )}
            </div>
            <div className="modal-foot">
              <span />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={attemptDismiss} disabled={busy}>Cancel</button>
                <button className="btn primary" onClick={submit} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
